import { Box, Typography } from '@mui/material';
import { DataGrid, esES, type GridColDef, type GridSelectionModel } from '@mui/x-data-grid';
import SearchTextField from 'Components/SeachTextField/SearchTextField';
import useTranslation from 'Hooks/useTranslation';
import { colors } from 'Constants/Colors';
import type { EligibleEmployeeRow } from '../../Hooks/useEligibleEmployeesForApproval';
import { styles } from './styles';

interface Props {
  rows: EligibleEmployeeRow[];
  totalEmployees: number;
  loading: boolean;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  search: string;
  onSearchChange: (value: string) => void;
  selectionModel: GridSelectionModel;
  onSelectionChange: (model: GridSelectionModel) => void;
  lockedRowId?: string | null;
}

const EmployeeSelectionPanel = ({
  rows, totalEmployees, loading, page, pageSize, onPageChange, onPageSizeChange,
  search, onSearchChange, selectionModel, onSelectionChange, lockedRowId,
}: Props) => {
  const { t2 } = useTranslation('EmployeeAssess');

  const columns: GridColDef[] = [
    { field: 'payrollNum', headerName: t2('PayrollNum'), flex: 0.7 },
    { field: 'fullName', headerName: t2('Employee'), flex: 1.5 },
    { field: 'department', headerName: t2('department_name') ?? 'Departamento', flex: 1 },
    { field: 'jobTitle', headerName: t2('job_title_name') ?? 'Puesto', flex: 1 },
    { field: 'terminal', headerName: t2('terminal_name') ?? 'Terminal', flex: 1 },
  ];

  return (
    <Box sx={styles.panelColumn}>
      <Box sx={styles.searchRow}>
        <SearchTextField
          value={search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSearchChange(e.target.value)}
          placeholder={t2('search_employee') ?? 'Buscar empleado'}
        />
      </Box>

      <Box
        sx={{
          ...styles.dataGridContainer,
          '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 'bold', color: colors.BLACK },
          '& .MuiCheckbox-root.Mui-checked': { color: colors.GREEN },
          '& .MuiCheckbox-root.MuiCheckbox-indeterminate': { color: colors.GREEN },
        }}
      >
        {!loading && rows.length === 0 && !search ? (
          <Typography sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
            {t2('no_eligible_employees')}
          </Typography>
        ) : (
          <DataGrid
            autoHeight
            rowHeight={40}
            density="compact"
            rows={rows}
            columns={columns}
            loading={loading}
            checkboxSelection
            isRowSelectable={params => !lockedRowId || params.row.id !== lockedRowId}
            disableSelectionOnClick
            disableColumnMenu
            rowCount={totalEmployees}
            paginationMode="server"
            page={page}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            rowsPerPageOptions={[15, 25, 50]}
            selectionModel={selectionModel}
            onSelectionModelChange={onSelectionChange}
            localeText={esES.components.MuiDataGrid.defaultProps.localeText}
          />
        )}
      </Box>
    </Box>
  );
};

export default EmployeeSelectionPanel;
